import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "modal-mask"
}
const _hoisted_2 = { class: "modal-wrapper" }
const _hoisted_3 = { class: "modal-dialog modal-xl modal-dialog-scrollable" }
const _hoisted_4 = { class: "modal-content" }
const _hoisted_5 = { class: "modal-body" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { key: 1 }
const _hoisted_8 = { class: "dropdown" }
const _hoisted_9 = {
  class: "btn btn-outline-secondary dropdown-toggle",
  "data-toggle": "dropdown"
}
const _hoisted_10 = {
  class: "dropdown-menu",
  style: {"max-height":"300px","overflow-y":"auto"}
}
const _hoisted_11 = ["onClick"]

import { ref, computed, watch, nextTick } from 'vue'
    import type { PropType } from 'vue'

    import type { WeightsComparerReadModel } from "@Criteria/Weights/Domain/readmodels"
    import type { CriterionReadModel } from "@Criteria/Hierarchies/Domain/readmodels"

    import { KendoChart, KendoChartDataSerie, KendoChartOptions } from "@Kendo/Components/KendoChart"
    import { ellipsis, getDistinctColor, roundDecimal } from "@common/Common"

    // ---------------- props / emits ----------------

    
export default /*@__PURE__*/_defineComponent({
  __name: 'WeightsComparerDialog',
  props: {
        propOpened: { type: Boolean, required: true, default: false },
        propRm: { type: Object as PropType<WeightsComparerReadModel | null>, default: null }
    },
  emits: ["close-dialog"],
  setup(__props, { emit: __emit }) {

    const props = __props

    const emit = __emit

    // ---------------- local state ----------------

    const chart = ref<KendoChart | null>(null)
    const selectedContextId = ref<number | null>(null)

    // ---------------- computed ----------------

    const opened = computed(() => props.propOpened)
    const rm = computed(() => props.propRm)

    const context = computed(() =>
        rm.value?.Criteria.find(a => a.CriterionId === selectedContextId.value)
    )

    const parents = computed(() =>
        rm.value
            ? rm.value.Criteria.filter(a =>
                rm.value!.Criteria.some(b => b.ParentId === a.CriterionId))
            : []
    )

    // ---------------- watchers ----------------

    // initialize context when rm arrives
    watch(rm, (val) =>
    {
        if (val && selectedContextId.value == null)
        {
            selectedContextId.value = val.ContextId ?? val.Criteria[0]?.CriterionId ?? null
        }
    })

    // re-render chart when context changes
    watch(selectedContextId, async (val) =>
    {
        if (val != null)
        {
            await nextTick()
            renderChart()
        }
    })

    watch(opened, async (val) =>
    {
        if (val && selectedContextId.value != null)
        {
            await nextTick()
            renderChart()
        }

        if (!val)
        {
            selectedContextId.value = null
        }
    })


    // ---------------- actions ----------------

    function setContext(c: CriterionReadModel)
    {
        selectedContextId.value = c.CriterionId
    }

    function close()
    {
        emit('close-dialog')
    }

    // ---------------- chart rendering ----------------

    function renderChart()
    {
        if (!rm.value || selectedContextId.value == null) return

        if (chart.value)
        {
            chart.value.Destroy?.()
            chart.value = null
        }


        const options = new KendoChartOptions()
        options.SetBarSeriesType()
        options.EnableSeriesStack100()
        options.SetCategoryAxis(false, false)
        options.SetValueAxis(null, false, false)
        options.SetLegend(true, "bottom", 35)
        options.SetTooltip(true, "<small>#= dataItem.Category #</small><br> #= dataItem.CriterionName #: #= value #%")

        const c = KendoChart.CreateChart(options)
        chart.value = c

        const subcriteria = rm.value.Criteria.filter(a => a.ParentId === selectedContextId.value)

        const series: KendoChartDataSerie[] = subcriteria.map((crit, i) =>
        {
            const dataItems = rm.value!.WeightsSources.map(ws =>
            {
                const weight = ws.Weights.find(a => a.CriterionId === crit.CriterionId)!.LocalWeight
                const value = roundDecimal(100 * weight, 0)

                return {
                    Category: ellipsis(ws.Name, 40),
                    CriterionName: crit.Name,
                    Value: value
                }
            })

            const serie = new KendoChartDataSerie()
            serie.SetName(crit.Name)
            serie.SetField("Value")
            serie.SetCategoryField("Category")
            serie.SetColor(getDistinctColor(i))
            serie.SetData(dataItems)

            return serie
        })

        if (series.length > 0)
        {
            series[0].SetLabels(true, "#= category #", "1rem Arial", "insideBase")
            series[0].SetLabelsColor("#fff")
        }

        c.SetDataSeries(series)

        const minHeightBars = rm.value.WeightsSources.length * 60
        const minVhPxl = 300
        c.SetHeight(Math.max(minHeightBars, minVhPxl))

        c.Render1('Weights')
    }

return (_ctx: any,_cache: any) => {
  return (opened.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", { class: "modal-header" }, [
                _cache[0] || (_cache[0] = _createElementVNode("h5", { class: "modal-title" }, "Weights comparison", -1 /* CACHED */)),
                _createElementVNode("button", {
                  onClick: close,
                  type: "button",
                  class: "close"
                }, "×")
              ]),
              _createElementVNode("div", _hoisted_5, [
                (!rm.value)
                  ? (_openBlock(), _createElementBlock("h2", _hoisted_6, [...(_cache[1] || (_cache[1] = [
                      _createElementVNode("i", { class: "fa fa-spinner fa-spin" }, null, -1 /* CACHED */),
                      _createTextVNode(" Loading...", -1 /* CACHED */)
                    ]))]))
                  : _createCommentVNode("v-if", true),
                (rm.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createElementVNode("div", _hoisted_8, [
                        _createElementVNode("button", _hoisted_9, _toDisplayString(context.value?.Name), 1 /* TEXT */),
                        _createElementVNode("div", _hoisted_10, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(parents.value, (c) => {
                            return (_openBlock(), _createElementBlock("a", {
                              class: "dropdown-item",
                              key: c.CriterionId,
                              onClick: ($event: any) => (setContext(c))
                            }, [
                              _createElementVNode("span", null, _toDisplayString(c.Name), 1 /* TEXT */),
                              _cache[2] || (_cache[2] = _createElementVNode("br", null, null, -1 /* CACHED */)),
                              _createElementVNode("small", null, _toDisplayString(c.Path), 1 /* TEXT */)
                            ], 8 /* PROPS */, _hoisted_11))
                          }), 128 /* KEYED_FRAGMENT */))
                        ])
                      ]),
                      _cache[3] || (_cache[3] = _createElementVNode("br", null, null, -1 /* CACHED */)),
                      _cache[4] || (_cache[4] = _createElementVNode("div", { id: "Chart-Weights" }, null, -1 /* CACHED */))
                    ]))
                  : _createCommentVNode("v-if", true)
              ]),
              _createElementVNode("div", { class: "modal-footer" }, [
                _createElementVNode("button", {
                  onClick: close,
                  class: "btn btn-outline-secondary"
                }, "Close")
              ])
            ])
          ])
        ])
      ]))
    : _createCommentVNode("v-if", true)
}
}

})